import React, { useState, useEffect } from "react";
import {
  PanelContainer,
  FilterBar,
  ListContainer,
  MessageItem,
  Header,
  TextContainer,
  TimeRange,
  Text,
} from "./ConversationPanel.styles";
import { Message } from "./types";
import DropDown from "components/dropdown/DropDown";
import {
  ActionType,
  CommonActionBar,
  DEFAULT_ACTION_STYLES,
} from "../../components/ActionBar";
import { formatTime } from "../../../../../utils/formatTime";
import SpeakerEmotionBadge from "components/SpeakerEmotionBadge";
import { formatSpeakerName } from "utils/formatSpeaker";
import SearchInputComponent from "components/common/SearchInput";
import { ProcessResult as VUResult } from "types/output_vu";
import { ProcessResultVh as VHResult } from "types/output_vh";
import { useMediaStore } from "store/mediaStore";

interface ConversationPanelProps {
  result: VUResult | VHResult | null;
  onCopy: () => void;
  onShare: () => void;
  onEdit: () => void;
  onDownload: () => void;
  onLike: () => void;
  onDislike: () => void;
  feedbackStatus?: "positive" | "negative" | null;
}

// 감정 한글 변환 함수
const convertEmotionToKorean = (emotion: string): string => {
  const emotionMap: { [key: string]: string } = {
    NEUTRAL: "중립",
    HAPPINESS: "기쁨",
    ANGRY: "화남",
    SADNESS: "슬픔",
    SURPRISE: "놀람",
  };
  return emotionMap[emotion] || "중립";
};

const ConversationPanel: React.FC<ConversationPanelProps> = ({
  result,
  onCopy,
  onShare,
  onEdit,
  onDownload,
  onLike,
  onDislike,
  feedbackStatus,
}) => {
  const { currentTime, setSeekTo } = useMediaStore();
  const [speakerFilter, setSpeakerFilter] = useState<string>("");
  const [emotionFilter, setEmotionFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [speakerOptions, setSpeakerOptions] = useState<string[]>(["전체"]);
  const [emotionOptions, setEmotionOptions] = useState<string[]>(["전체"]);

  useEffect(() => {
    if (!result || !result.output?.utterances) {
      setMessages([]);
      return;
    }

    const allMessages = result.output.utterances.map((u, index) => {
      return {
        id: `${u.speaker}-${index}`,
        speaker: formatSpeakerName(String(u.speaker)),
        emotion: u.emotion ? convertEmotionToKorean(u.emotion) : "중립",
        timeRange: `${formatTime(u.start)} ~ ${formatTime(u.end)}`,
        text: u.text,
      };
    });

    allMessages.sort((a, b) => {
      const aStart = parseFloat(a.timeRange.split(" ~ ")[0].replace(":", "."));
      const bStart = parseFloat(b.timeRange.split(" ~ ")[0].replace(":", "."));
      return aStart - bStart;
    });

    setMessages(allMessages);

    const speakers = Array.from(new Set(allMessages.map((m) => m.speaker)));
    setSpeakerOptions(["전체", ...speakers]);

    const emotions = Array.from(new Set(allMessages.map((m) => m.emotion)));
    setEmotionOptions(["전체", ...emotions]);
  }, [result]);

  const handleSpeakerChange = (option: string) => {
    setSpeakerFilter(option === "전체" ? "" : option);
  };

  const handleEmotionChange = (option: string) => {
    setEmotionFilter(option === "전체" ? "" : option);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filtered = messages.filter((msg) => {
    const matchesSpeaker = !speakerFilter || msg.speaker === speakerFilter;
    const matchesEmotion = !emotionFilter || msg.emotion === emotionFilter;
    const matchesSearch =
      !searchTerm || msg.text.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSpeaker && matchesEmotion && matchesSearch;
  });

  const displaySpeaker = speakerFilter || "화자별";

  const leftActions = [
    // {
    //   type: "copy" as ActionType,
    //   label: "복사",
    //   onClick: onCopy,
    //   ...DEFAULT_ACTION_STYLES.copy,
    // },
    // {
    //   type: "share" as ActionType,
    //   label: "공유",
    //   onClick: onShare,
    //   ...DEFAULT_ACTION_STYLES.share,
    // },
    // {
    //   type: "edit" as ActionType,
    //   label: "수정",
    //   onClick: onEdit,
    //   ...DEFAULT_ACTION_STYLES.edit,
    // },
    {
      type: "like" as ActionType,
      label: "",
      onClick: onLike,
      ...DEFAULT_ACTION_STYLES.like,
      isActive: feedbackStatus === "positive",
    },
    {
      type: "dislike" as ActionType,
      label: "",
      onClick: onDislike,
      ...DEFAULT_ACTION_STYLES.dislike,
      isActive: feedbackStatus === "negative",
    },
  ];

  const rightActions = [
    {
      type: "download" as ActionType,
      label: "다운로드",
      onClick: onDownload,
      ...DEFAULT_ACTION_STYLES.download,
    },
  ];

  const parseTime = (timeStr: string) => {
    const parts = timeStr.split(":").map(parseFloat);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    return parts[0] || 0;
  };

  const activeIndex = filtered.findIndex((message, index) => {
    const nextMessage = filtered[index + 1];
    const startTime = parseTime(message.timeRange.split(" ~ ")[0]);

    if (nextMessage) {
      const nextStartTime = parseTime(nextMessage.timeRange.split(" ~ ")[0]);
      return currentTime >= startTime && currentTime < nextStartTime;
    } else {
      // 마지막 아이템의 경우, end time까지 포함
      const endTime = parseTime(message.timeRange.split(" ~ ")[1]);
      return currentTime >= startTime && currentTime <= endTime;
    }
  });

  const handleItemClick = (time: number) => {
    setSeekTo(time);
  };

  return (
    <PanelContainer>
      <FilterBar>
        <DropDown
          options={speakerOptions}
          selectedOption={displaySpeaker}
          onSelect={handleSpeakerChange}
        />
        <DropDown
          options={emotionOptions}
          selectedOption={emotionFilter || "감정별"}
          onSelect={handleEmotionChange}
        />
        <SearchInputComponent
          placeholder="검색어를 입력하세요."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </FilterBar>
      <ListContainer>
        {filtered.map((message, index) => {
          const [startTimeStr, endTimeStr] = message.timeRange.split(" ~ ");
          const startTime = parseTime(startTimeStr);
          const isActive = index === activeIndex;

          return (
            <MessageItem
              key={message.id}
              $isActive={isActive}
              onClick={() => handleItemClick(startTime)}
            >
              <Header>
                <SpeakerEmotionBadge
                  speaker={message.speaker}
                  emotion={message.emotion}
                  showIcon={true}
                />
              </Header>
              <TextContainer>
                <TimeRange>{message.timeRange}</TimeRange>
                <Text>{message.text}</Text>
              </TextContainer>
            </MessageItem>
          );
        })}
      </ListContainer>
      <CommonActionBar leftActions={leftActions} rightActions={rightActions} />
    </PanelContainer>
  );
};

export default ConversationPanel;
